import { DateType } from 'api/models';
import {
  ResourceIdentifier,
  ResourceType,
} from 'api/resource-management/types';
import { aggregationIdForUser } from 'queries/resource-management';
import { SchedulerResourceType } from '../resource-types';
import { ViewPresetId } from '../scheduler-config/view-presets/constants';

export interface UserAsLeafResourceProps {
  projectId: number;
  userId: number;
  roleId?: number;
  referenceTeamId?: number;
}

export interface ProjectAsLeafResourceProps {
  projectId: number;
  userId: number;
  roleId?: number;
  referenceTeamId?: number;
}

export const projectIdFormat = (id: number): string => `project-${id}`;
export const userIdFormat = (id: number): string => `user-${id}`;
export const teamIdFormat = (id: number): string => `team-${id}`;
export const roleIdFormat = (id: number): string => `role-${id}`;
export const companyIdFormat = (id: number): string => `company-${id}`;
export const referenceTeamIdFormat = (id: number): string =>
  `referenceTeam-${id}`;
export const aggregationId = (
  type: ResourceType,
  projectId: number,
  roleId?: number,
  referenceTeamId?: number,
): string =>
  `[project-${projectId}${
    type === ResourceType.User
      ? `${roleId ? `_role-${roleId}` : ''}${
          referenceTeamId ? `_referenceTeam-${referenceTeamId}` : ''
        }`
      : ''
  }`;
export const resourceIdForRole = (projectId: number, roleId: number): string =>
  `${projectIdFormat(projectId)}_${roleIdFormat(roleId)}`;
export const resourceIdForProject = (projectId: number): string =>
  projectIdFormat(projectId);
export const resourceIdForUser = (userId: number): string =>
  userIdFormat(userId);
export const resourceIdForTeam = (teamId: number): string =>
  teamIdFormat(teamId);
export const resourceIdForCompany = (companyId: number): string =>
  companyIdFormat(companyId);
export const resourceIdForProjectAsLeaf = ({
  projectId,
  userId,
  roleId,
  referenceTeamId,
}: ProjectAsLeafResourceProps): string =>
  `${projectIdFormat(projectId)}_${userIdFormat(userId)}${
    roleId ? `_${roleIdFormat(roleId)}` : ''
  }${referenceTeamId ? `_${referenceTeamIdFormat(referenceTeamId)}` : ''}`;
export const resourceIdForUserAsLeaf = ({
  projectId,
  userId,
  roleId,
  referenceTeamId,
}: UserAsLeafResourceProps): string =>
  `${projectIdFormat(projectId)}_${userIdFormat(userId)}${
    roleId ? `_${roleIdFormat(roleId)}` : ''
  }${referenceTeamId ? `_${referenceTeamIdFormat(referenceTeamId)}` : ''}`;
export const resourceIdForTeamAsLeaf = (
  projectId: number,
  teamId: number,
): string => `${projectIdFormat(projectId)}_${teamIdFormat(teamId)}`;
export const resourceIdForAssignUserOrTeam = (projectId: number): string =>
  `${projectIdFormat(projectId)}_assign_user_or_team`;
export const resourceIdForShowAllProjects = (roleId: number): string =>
  `${roleIdFormat(roleId)}_show_all_projects`;
export const resourceIdForAssignProject = (
  parentType: ResourceType,
  userIdOrTeamId: number,
): string =>
  `${
    parentType === ResourceType.User
      ? userIdFormat(userIdOrTeamId)
      : teamIdFormat(userIdOrTeamId)
  }_assign_project`;

export const resourceIdForTeamHeader = (): string =>
  SchedulerResourceType.TeamHeader;

export const resourceIdForPartnerHeader = (): string => 'partner_header';

export const resourceIdForExpandableRole = (
  roleId: number,
  companyId?: number,
): string =>
  `${roleIdFormat(roleId)}${
    companyId != null ? `_${companyIdFormat(companyId)}` : ''
  }`;
export const resourceIdForDemandResource = (
  roleId: number,
  resourceType: SchedulerResourceType,
): string => `${resourceType}_${roleId}`;

export const idForTeamAllocationSummaryEvent = (
  roleId: number,
  startDate: DateType,
  viewPreset: ViewPresetId,
  companyId?: number,
): string =>
  `role-${roleId}_${
    companyId != null ? `company-${companyId}_` : ''
  }${viewPreset}_${new Date(startDate).valueOf()}`;
export const idForCompanyAllocationSummaryEvent = (
  companyId: number,
  startDate: DateType,
  viewPreset: ViewPresetId,
): string =>
  `company-${companyId}_${viewPreset}_${new Date(startDate).valueOf()}`;

export const idForUserAllocationSummaryEvent = (
  userId: number,
  startDate: DateType,
  viewPreset: ViewPresetId,
): string => `user-${userId}_${viewPreset}_${new Date(startDate).valueOf()}`;

export const idForTimeoffAsSummaryEvent = (
  userId: number,
  timeOffId: number,
): string => `user-${userId}_${timeOffId}`;

export const idForHolidayAsSummaryEvent = (
  userId: number,
  holidayId: number,
): string => `user-${userId}_holiday-${holidayId}`;

export const getKeyForResourceAggregation = (
  resourceIdentifier: ResourceIdentifier,
): string => {
  if (resourceIdentifier.type === ResourceType.User) {
    return `user-${resourceIdentifier.userId}`;
  }

  return `team-${resourceIdentifier.teamId}`;
};

export const getKeyForProjectAggregation = (
  projectId: number,
  resourceIdentifier: ResourceIdentifier,
): string => {
  if (resourceIdentifier.type === ResourceType.User) {
    return aggregationIdForUser(
      projectId,
      resourceIdentifier.roleId,
      resourceIdentifier.referenceTeamId,
    );
  }

  return `project-${projectId}`;
};
